import React, { useState, useEffect, useRef } from "react";
import { Dialog, Input, Dropdown } from "../utils";
import { CircularProgress } from "@mui/material";
import { capitalizeCamelCase } from "../../services/functions";
import { useSendCommandMutation } from "../../api/deviceSlice";
import { useTriggerLedMutation } from "../../api/vmsSlice";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
const TwoLineTypeOption = [
	{ label: "CLEAR", value: "CLEAR" },
	{ label: "DISPLAY", value: "DISPLAY" },
];
const defaultCommandFormDataForLED = {
	deviceId: "",
	message: {
		type: "",
		lineOne: "",
		lineTwo: "",
	},
};
const defaultCommandFormData = {
	command: "",
	commandCode: "GPIO",
	power: "",
	antennaId: "",
};
function SendCommand({ onClose, open, deviceData }) {
	// console.log("deviceData", deviceData)
	const [commandFormDataForLED, setCommandFormDataForLED] = useState(defaultCommandFormDataForLED);
	const [commandFormData, setCommandFormData] = useState(defaultCommandFormData);
	const [handleSendCommand, { isLoading: isSendingCommand }] = useSendCommandMutation();
	const [triggerLedCommand, { isLoading: isSendingLedCommand }] = useTriggerLedMutation();
	const [apiResponse, setApiResponse] = useState({ error: null, message: "" });
	const [error, setError] = useState({});
	const timerRef = useRef(null);

	//For LED COMMAND DETAILS
	const twoLineTypeValue = TwoLineTypeOption.find((_) => _.value == commandFormDataForLED?.message?.type);
	const footerContent = isSendingLedCommand ? (
		<div>
			<CircularProgress size={20} />
			Waiting...
		</div>
	) : apiResponse.error === false ? (
		<div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
			<CheckCircleIcon size={20} style={{ color: "green" }} />
			{apiResponse.message}
		</div>
	) : null;

	const isTwoLineLED = deviceData.deviceType == "led" && deviceData?.deviceSubType == "TWOLINE";
	const isCorrectReader = deviceData.deviceType == "rfidReader" && ["seuic", "customReader"].includes(deviceData?.deviceSubType);

	// Effect to clear footer after 5 seconds when API responds with success
	useEffect(() => {
		if (apiResponse.error === false) {
			timerRef.current = setTimeout(() => {
				setApiResponse({ error: null, message: "" });
			}, 5000);
		}

		return () => {
			if (timerRef.current) {
				clearTimeout(timerRef.current);
				timerRef.current = null;
			}
		};
	}, [apiResponse.error]);

	//FOR ALL OTHER COMMAND
	const matchedItem = deviceData?.logPattern?.find((item) => /^(gpi?o)$/i.test(item.commandCode || ""));
	const commandOptions = matchedItem
		? Object.entries(matchedItem)
				.filter(([key]) => !["commandCode", "logTime"].includes(key))
				.map(([key]) => ({
					label: capitalizeCamelCase(key),
					value: key,
				}))
		: [];
	const commandValue = commandOptions.find((opt) => opt.value === commandFormData.command) || null;
	const commandCodeValue = cmdTypeOpts.find((_) => _.value == commandFormData.commandCode) || null;
	const antennaIdOpts = deviceData?.antenna?.map((_) => ({ label: _.antennanumber, value: _.antennanumber })) || [];
	const antennaValue = antennaIdOpts.find((_) => _.value == commandFormData.antennaId);

	const handleClose = () => {
		setCommandFormDataForLED(defaultCommandFormDataForLED);
		setCommandFormData(defaultCommandFormData);
		setError({});
		onClose();
	};

	const handleClear = () => {
		setCommandFormDataForLED({
			message: {
				type: "",
				lineOne: "",
				lineTwo: "",
			},
		});
		setCommandFormData({
			command: "",
		});
		setApiResponse({ error: null, message: "" });
		// Clear any running timer
		if (timerRef.current) {
			clearTimeout(timerRef.current);
			timerRef.current = null;
		}
	};
	const handleCommandFormChange = (e) => {
		let { name, value, checked, type } = e.target;
		value = type === "checkbox" ? checked : value;
		if (isTwoLineLED) {
			setCommandFormDataForLED((old) => ({
				...old,
				message: {
					...old.message,
					[name]: value,
				},
			}));
		}
		// FOR RFID AND ALL OTHER
		if (isCorrectReader) {
			setCommandFormData((old) => ({
				...old,
				[name]: value,
			}));
		}
	};

	const onCommandDropdownChange = (option, name) => {
		let value = Array.isArray(option) ? option.map((_) => _.value) : option?.value || "";
		// FOR ONLY LED
		if (isTwoLineLED) {
			setCommandFormDataForLED((old) => ({
				...old,
				message: {
					...old.message,
					[name]: value,
				},
			}));
		}
		// FOR RFID AND ALL OTHER
		if (isCorrectReader) {
			setCommandFormData((old) => ({
				...old,
				[name]: value,
			}));
		}
	};

	const handleFormSubmit = async () => {
		let errors = {};
		// FOR ONLY LED
		if (isTwoLineLED) {
			const LEDPayload = {
				deviceId: deviceData?.imei,
				message: {
					type: commandFormDataForLED?.message?.type,
					...(commandFormDataForLED?.message?.type === "DISPLAY" ? { lineOne: commandFormDataForLED?.message?.lineOne, lineTwo: commandFormDataForLED?.message?.lineTwo } : {}),
				},
			};
			setApiResponse({ error: null, message: "" });
			// console.log("Payload", LEDPayload)
			try {
				const result = await triggerLedCommand(LEDPayload);
				const { data } = result;
				setApiResponse({ error: data?.error, message: data?.message || "" });
			} catch (err) {
				setApiResponse({ error: true, message: "Something went wrong" });
			}
		}
		// FOR RFID AND ALL OTHER
		if (isCorrectReader) {
			if (!commandFormData.commandCode) errors.commandCode = "Please select command code";
			if (commandFormData.commandCode == "GPIO" && !commandFormData.command) errors.command = "Please select command";
			if (commandFormData.commandCode == "AntennaPower" && !commandFormData.antennaId) errors.antennaId = "Please select Antenna";
			if (commandFormData.commandCode == "AntennaPower" && !commandFormData.power) errors.power = "Please enter power";
			// if (commandFormData.commandCode == "AntennaPower" && !commandFormData.power) errors.power = "P";
			if (commandFormData.commandCode == "AntennaPower" && (commandFormData.power < 0 || commandFormData.power > 30)) errors.power = "Power should be between 0 and 30";
			// console.log(errors)
			if (Object.keys(errors).length) {
				setError(errors);
			} else {
				setError({});
				const payload = {
					deviceId: deviceData?.imei,
					commandCode: commandFormData.commandCode,
					command: commandFormData?.command,
					antennaSetting: {
						antennaId: commandFormData.antennaId,
						power: Number(commandFormData.power),
					},
				};
				setApiResponse({ error: null, message: "" });
				// console.log("Payload", payload);
				try {
				    const result = await handleSendCommand(payload);
				    const { data } = result;
				    setApiResponse({ error: data?.error, message: data?.message || "" });
				} catch (err) {
				    setApiResponse({ error: true, message: "Something went wrong" });
				}
			}
		}
	};
	return (
		<Dialog size="xs" open={open} handleClose={handleClose} handleCancel={handleClear} handleSubmit={handleFormSubmit} title="Send Command" CancelText="Clear" SubmitText="Send Command">
			<div style={{ display: "grid", gridTemplateColumns: "150px 1fr", gap: "8px 16px", alignItems: "center", marginBottom: "16px" }}>
				<div style={{ fontWeight: "bold" }}>Device Name:</div>
				<div>{deviceData?.displayName || "-"}</div>

				<div style={{ fontWeight: "bold" }}>Device Id:</div>
				<div>{deviceData?.imei || "-"}</div>

				<div style={{ fontWeight: "bold" }}>Device Type:</div>
				<div>{capitalizeCamelCase(deviceData?.deviceType) || "-"}</div>

				<div style={{ fontWeight: "bold" }}>Device Subtype:</div>
				<div>{capitalizeCamelCase(deviceData?.deviceSubType) || "-"}</div>

				{isCorrectReader ? (
					<>
						<div style={{ fontWeight: "bold" }}>Command Code :</div>
						<Dropdown id="commandCode" name="commandCode" label="" onChange={onCommandDropdownChange} options={cmdTypeOpts} value={commandCodeValue} required error={error.commandCode} />
					</>
				) : null}
			</div>
			<form className="">
				<div className="form-inputs mt-2">
					{/* FOR LED ONLY */}
					{isTwoLineLED && (
						<>
							<Dropdown id="type" name="type" label="Type" onChange={onCommandDropdownChange} options={TwoLineTypeOption} value={twoLineTypeValue} required />
							{deviceData?.deviceType == "led" && deviceData?.deviceSubType == "TWOLINE" && commandFormDataForLED?.message?.type == "DISPLAY" && (
								<>
									<Input label="Line One" placeholder="Enter Line One Message" name="lineOne" value={commandFormDataForLED?.message?.lineOne} onChange={handleCommandFormChange} />
									<Input label="Line Two" placeholder="Enter Line Two Message" name="lineTwo" value={commandFormDataForLED?.message?.lineTwo} onChange={handleCommandFormChange} />
								</>
							)}
						</>
					)}
					{/* FOR RFID AND ALL OTHER */}
					{isCorrectReader && (
						<>
							{commandFormData.commandCode == "GPIO" && (
								<Dropdown
									id="command"
									name="command"
									label="Command"
									onChange={onCommandDropdownChange}
									options={commandOptions}
									value={commandValue}
									required
									error={error.command}
								/>
							)}
							{commandFormData.commandCode == "AntennaPower" && (
								<>
									<Dropdown
										id="antennaId"
										name="antennaId"
										label="Antenna"
										onChange={onCommandDropdownChange}
										options={antennaIdOpts}
										value={antennaValue}
										required
										error={error.antennaId}
									/>
									<Input
										id="power"
										name="power"
										label="Power"
										onChange={handleCommandFormChange}
										value={commandFormData.power}
										required
										type="number"
										min="0"
										max="30"
										error={error.power}
									/>
								</>
							)}
						</>
					)}
				</div>
			</form>
			<div style={{ display: "flex", justifyContent: "center", alignItems: "end", marginTop: "1rem" }}>{footerContent}</div>
		</Dialog>
	);
}

export default SendCommand;

const cmdTypeOpts = [
	{ label: "GPIO", value: "GPIO" },
	{ label: "Antenna Power", value: "AntennaPower" },
];
